import React from "react"
import type { TFunction } from "i18next"
import type { FormDescriptionImage } from "@f2e/shared/types"
import { FieldDescription } from "@f2e/ui/components/InteractionRequestForm"

import { Label, RadioGroup, RadioGroupItem } from "@f2e/ui/components/base"
import { BaseFieldEditor, BaseFieldProps } from "./BaseFieldEditor"

import { OptionsEditor } from "./OptionsEditor"

export type RadioField = BaseFieldProps<{
  type: "radio"
  name: string
  question: string
  label: string
  placeholder: string
  required: boolean
  options?: { label: string; value: string }[]
}>

export const RadioFieldEditor: React.FC<RadioField> = ({
  field,
  onChange,
  disabled,
}) => {
  return (
    <>
      <BaseFieldEditor
        field={field}
        onChange={onChange}
        disabled={disabled}
        showPlaceholder={false}
      />
      <OptionsEditor field={field} onChange={onChange} disabled={disabled} />
    </>
  )
}

export default RadioFieldEditor

export const RadioFieldRenderer: React.FC<{
  field: {
    id?: string
    name?: string
    question: string
    options?: { label: string; value: string }[]
    required: boolean
    helpText?: string
    descriptionImage?: FormDescriptionImage
  }
  mode: "preview" | "runtime"
  value?: string
  onChange?: (v: string) => void
  disabled?: boolean
  error?: string
  contextId?: string // Unique context identifier to prevent React from reusing component instances
}> = ({ field, mode, value, onChange, disabled, error, contextId }) => {
  const opts = field.options || []
  const isPreview = mode === "preview"
  const [local, setLocal] = React.useState<string | undefined>(undefined)
  const val = isPreview ? local : value
  // Use field id or name to make radio ids unique per field instance
  const fieldId = field.id || field.name || "radio-field"
  const setVal = (v: string) => {
    if (isPreview) setLocal(v)
    else onChange?.(v)
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs">
        {field.question}{" "}
        {field.required ? <span className="text-red-500">*</span> : null}
      </Label>
      <FieldDescription
        helpText={field.helpText}
        descriptionImage={field.descriptionImage}
      />
      <RadioGroup
        value={val}
        onValueChange={setVal}
        disabled={disabled}
        className={error ? "rounded-md border border-red-500 p-2" : undefined}
      >
        {opts.map((o, idx) => {
          const id = `radio-${fieldId}-${contextId || "default"}-${o.value || idx}`
          return (
            <div className="flex items-center gap-2" key={o.value || idx}>
              <RadioGroupItem value={o.value} id={id} />
              <Label htmlFor={id} className="text-sm font-normal">
                {o.label}
              </Label>
            </div>
          )
        })}
      </RadioGroup>
      {error ? <div className="text-xs text-red-500">{error}</div> : null}
    </div>
  )
}

export function serializeRadioField(
  field: { options?: { label: string; value: string }[] },
  raw: unknown
): unknown {
  const options = Array.isArray(field.options) ? field.options : []
  const found = options.find((o) => o.value === raw)
  return found ? found.label : raw
}

export function validateRadioField(
  field: { name: string; required?: boolean },
  value: unknown,
  t: TFunction
): string | undefined {
  if (!field.required) return undefined
  const str = value === undefined || value === null ? "" : String(value)
  if (str.trim() === "")
    return t("workspace.agent.basicForm.formBuilder.validation.required")
  return undefined
}
